/**
 * UsageLimitReached Component
 * 
 * Displays when user has reached their analysis limit.
 * Shows clear message with VALUE-BASED MESSAGING and upgrade CTAs.
 * 
 * CONVERSION OPTIMIZED:
 * - Shows what they gained (deals analyzed, bad deals avoided)
 * - Shows what they're missing
 * - Clear upgrade path
 * 
 * Plans: Free (3 lifetime), Investor ($49/50mo), Pro ($99/200mo)
 * Bilingual: EN/ES
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Zap, ArrowRight, BarChart3, Shield, TrendingUp, CheckCircle2, XCircle } from 'lucide-react';
import { analyticsAPI } from '../api';

const translations = {
  en: {
    limitReached: "You've reached your analysis limit",
    freeMessage: "You've used all 3 free analyses.",
    
    // Value-based messaging
    valueTitle: "Here's what you gained",
    dealsAnalyzed: "Deals Analyzed",
    badDealsAvoided: "Risky Deals Avoided",
    potentialSavings: "Potential Savings",
    valueMessage: "You've already protected yourself from {passCount} deals that don't meet your criteria. Keep this protection going.",
    noPassMessage: "You've analyzed {count} deals — this due diligence saves you from costly mistakes.",
    
    // What you're missing
    missingTitle: "Without upgrading, you can't:",
    missingAnalyze: "Analyze new properties",
    missingCompare: "Compare multiple deals",
    missingUpdates: "Get updated market data",
    
    // Upgrade benefits
    upgradeTitle: "Upgrade to keep analyzing",
    investorFeature1: "50 analyses per month",
    investorFeature2: "Save & compare deals",
    investorFeature3: "Email support",
    
    viewPricing: "View Plans",
    stillAvailable: "You can still:",
    viewHistory: "View your past analyses",
    reclassify: "Re-analyze saved deals with different criteria",
    shareDeals: "Share and export your existing reports",
    usage: "Your Usage",
    viewSavedDeals: "View My Saved Deals →"
  },
  es: {
    limitReached: "Has alcanzado tu límite de análisis",
    freeMessage: "Has usado tus 3 análisis gratuitos.",
    
    // Value-based messaging
    valueTitle: "Esto es lo que ganaste",
    dealsAnalyzed: "Tratos Analizados",
    badDealsAvoided: "Tratos Riesgosos Evitados",
    potentialSavings: "Ahorros Potenciales",
    valueMessage: "Ya te protegiste de {passCount} tratos que no cumplen tus criterios. Mantén esta protección.",
    noPassMessage: "Has analizado {count} tratos — esta diligencia te salva de errores costosos.",
    
    // What you're missing
    missingTitle: "Sin actualizar, no puedes:",
    missingAnalyze: "Analizar nuevas propiedades",
    missingCompare: "Comparar múltiples tratos",
    missingUpdates: "Obtener datos de mercado actualizados",
    
    // Upgrade benefits
    upgradeTitle: "Actualiza para seguir analizando",
    investorFeature1: "50 análisis por mes",
    investorFeature2: "Guardar y comparar tratos",
    investorFeature3: "Soporte por email",
    
    viewPricing: "Ver Planes",
    stillAvailable: "Todavía puedes:",
    viewHistory: "Ver tus análisis anteriores",
    reclassify: "Re-analizar tratos guardados con diferentes criterios",
    shareDeals: "Compartir y exportar tus reportes existentes",
    usage: "Tu Uso",
    viewSavedDeals: "Ver Mis Tratos Guardados →"
  }
};

const UsageLimitReached = ({ 
  usage, 
  dealStats = null,
  userId = null,
  onViewPricing, 
  onViewHistory,
  lang = 'en' 
}) => {
  const t = translations[lang] || translations.en;
  const [tracked, setTracked] = useState(false);
  
  const planType = usage?.plan_type || 'FREE';
  const isFree = planType === 'FREE';
  
  // Deal stats for value messaging
  const analysesCount = usage?.analyses_used || 0;
  const passCount = dealStats?.PASS || dealStats?.NO_PASS || 0;
  
  // Estimate potential savings (avg bad deal costs ~$10k-50k in losses)
  const potentialSavings = passCount > 0 ? passCount * 25000 : analysesCount * 5000;
  
  // Track limit reached event
  useEffect(() => {
    if (userId && !tracked) {
      analyticsAPI.trackLimitReached(
        userId, 
        planType, 
        usage?.analyses_used || 0, 
        usage?.analyses_limit || 3
      ).catch(() => {});
      setTracked(true);
    }
  }, [userId, planType, usage, tracked]);
  
  // Message is always free message now (trial removed)
  const message = t.freeMessage;
  
  // Value message
  const getValueMessage = () => {
    if (passCount > 0) {
      return t.valueMessage.replace('{passCount}', passCount.toString());
    }
    return t.noPassMessage.replace('{count}', analysesCount.toString());
  };
  
  // Handle upgrade click with tracking
  const handleUpgradeClick = () => {
    if (userId) {
      analyticsAPI.trackUpgradeClick(userId, 'limit_modal', planType, {
        analyses_used: analysesCount,
        pass_count: passCount
      }).catch(() => {});
    }
    onViewPricing();
  };
  
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="usage-limit-reached">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {t.limitReached}
            </h2>
            <p className="text-amber-100 text-sm max-w-sm mx-auto">
              {message}
            </p>
          </div>
          
          {/* VALUE SECTION - What they gained */}
          <div className="px-6 py-5 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-emerald-800">{t.valueTitle}</h3>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center p-2 bg-white rounded-lg border border-emerald-200">
                <div className="text-xl font-bold text-slate-800">{analysesCount}</div>
                <div className="text-xs text-slate-500">{t.dealsAnalyzed}</div>
              </div>
              {passCount > 0 && (
                <div className="text-center p-2 bg-white rounded-lg border border-red-200">
                  <div className="text-xl font-bold text-red-600">{passCount}</div>
                  <div className="text-xs text-slate-500">{t.badDealsAvoided}</div>
                </div>
              )}
              <div className="text-center p-2 bg-white rounded-lg border border-emerald-200">
                <div className="text-xl font-bold text-emerald-600">${(potentialSavings / 1000).toFixed(0)}k+</div>
                <div className="text-xs text-slate-500">{t.potentialSavings}</div>
              </div>
            </div>
            
            <p className="text-sm text-emerald-700">
              {getValueMessage()}
            </p>
          </div>
          
          {/* MISSING SECTION - What they can't do */}
          <div className="px-6 py-4 bg-red-50 border-b border-red-200">
            <p className="text-sm font-medium text-red-700 mb-2">{t.missingTitle}</p>
            <ul className="space-y-1.5">
              <li className="flex items-center gap-2 text-sm text-red-600">
                <XCircle className="w-4 h-4" />
                {t.missingAnalyze}
              </li>
              <li className="flex items-center gap-2 text-sm text-red-600">
                <XCircle className="w-4 h-4" />
                {t.missingCompare}
              </li>
              <li className="flex items-center gap-2 text-sm text-red-600">
                <XCircle className="w-4 h-4" />
                {t.missingUpdates}
              </li>
            </ul>
          </div>
          
          {/* Usage Stats */}
          {usage && (
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600">{t.usage}</span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-slate-800">
                    {usage.analyses_used} / {usage.analyses_limit}
                  </span>
                  <span className="text-xs text-slate-500 ml-1">
                    {lang === 'en' ? 'analyses' : 'análisis'}
                  </span>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                  style={{ width: `${Math.min(100, usage.usage_percentage)}%` }}
                />
              </div>
            </div>
          )}
          
          {/* CTAs */}
          <div className="px-6 py-6 space-y-3">
            {/* View Pricing - Primary CTA */}
            <button
              onClick={handleUpgradeClick}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg shadow-indigo-200"
              data-testid="view-pricing-btn"
            >
              <Zap className="w-5 h-5" />
              {t.viewPricing}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          
          {/* What you can still do */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <p className="text-sm font-medium text-slate-700 mb-3">{t.stillAvailable}</p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                {t.viewHistory}
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                {t.reclassify}
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                {t.shareDeals}
              </li>
            </ul>
            
            {onViewHistory && (
              <button
                onClick={onViewHistory}
                className="mt-4 w-full px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                data-testid="view-history-btn"
              >
                {t.viewSavedDeals}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageLimitReached;
